import { types } from 'mobx-state-tree';
import { CaseStatus, CaseType, Panel, Step } from 'utils/enumerations';
import { TrackableModel } from '../trackableModel';
import { AttachmentModel } from './attachmentModel';
import { ApplicantBlockModel, BillingInformationBlockModel, DateTimeBlockModel, PurposeBlockModel } from './blocks';
import { CaseReferenceModel } from './caseReferenceModel';
import { PointGeometry, PolygonGeometry, PolylineGeometry } from './geometry';

const ApplicationModel = types
  .model('ApplicationModel', {
    gid: types.identifier,
    caseNumber: types.maybe(types.string),
    caseType: types.maybe(types.integer),
    applicant: types.optional(ApplicantBlockModel, {}),
    purpose: types.optional(PurposeBlockModel, {}),
    billingInformation: types.optional(BillingInformationBlockModel, {}),
    dateTimeInformation: types.optional(DateTimeBlockModel, {}),
    internalDiaryNumber: types.maybe(types.string),
    status: types.maybe(types.integer),
    statusMotivation: types.maybe(types.string),
    handlingOfficer: types.maybe(types.integer),
    handlingOfficerName: types.maybe(types.string),
    location: types.maybe(types.string),
    geometrySnapshot: types.maybe(types.string),
    approvedDate: types.maybe(types.string),
    registrationDate: types.maybe(types.string),
    modificationDate: types.maybe(types.string),
    additionalInformation: types.maybe(types.string),
    version: types.maybe(types.number),
    points: types.array(PointGeometry),
    polylines: types.array(PolylineGeometry),
    polygons: types.array(PolygonGeometry),
    attachments: types.array(AttachmentModel),
    childCases: types.array(CaseReferenceModel),
    parentCases: types.array(CaseReferenceModel),
    termsAndConditions: types.maybe(types.boolean),
    touchedPanelIndex: 0,
    touchedStep: 0
  })
  .volatile(() => ({
    mapState: {},
    geometriesHasChanged: false,
    currentStep: Step.Basic,
    currentPanel: Panel.Case,
    activeKey: undefined,
    form: undefined,
    showNewRegisterModal: false,
    isScreenshotEnabled: false,
    invalidGeometryType: undefined,
    continueToAfterScreenshot: -1,
    attachmentRemoved: false,
    showView: 'form',
    sketchVM: null,
    sendingInOrSavingCase: false
  }))
  .actions(self => ({
    setValues(changedValues) {
      for (let key in changedValues) {
        if (typeof self[key] === 'object') {
          if (self[key].setValues) {
            self[key].setValues(changedValues[key]);
          } else if (Array.isArray(self[key])) {
            self[key] = changedValues[key];
          } else {
            self[key] = { ...self[key], ...changedValues[key] };
          }
        } else {
          self[key] = changedValues[key];
        }
      }
    },
    addAttachment: newAttachment => {
      if (self.attachments.findIndex(attachment => attachment.gid === newAttachment.gid) === -1) {
        self.attachments.push(newAttachment);
      }
    },
    removeAttachment: fileGid => {
      const index = self.attachments.findIndex(attachment => attachment.gid === fileGid);
      if (index !== -1) {
        self.attachments.splice(index, 1);
        self.attachmentRemoved = true;
      }
    },
    setAttachmentRemoved: value => {
      self.attachmentRemoved = value;
    },
    setActiveKey: key => {
      const keyWithoutHash = key?.indexOf('#') > 0 ? key?.substr(0, key.indexOf('#')) : key;
      if (self.getPanelByKey) {
        const itemKeyPanel = self.getPanelByKey(keyWithoutHash);
        if (itemKeyPanel && itemKeyPanel !== self.currentPanel) {
          self.currentPanel = itemKeyPanel;
        }
      }
      self.activeKey = keyWithoutHash;
    },
    setSendingInOrSavingCase: value => {
      self.sendingInOrSavingCase = value;
    },
    setStatus: status => {
      self.status = status;
    },
    nextPanel: key => {
      self.setPanel(key);
    },
    setPanel: key => {
      self.currentPanel = key;
      if (self.setTouchedPanelIndex) {
        self.setTouchedPanelIndex(key);
      }
    },
    //setStep is used by goToStep after validation.
    setStep: (step, setFirstPanel) => {
      self.currentStep = step;
      if (setFirstPanel) {
        switch (self.caseType) {
          case CaseType.DiggingPermit:
            switch (step) {
              case Step.Basic:
                self.setPanel(Panel.Case);
                break;
              case Step.Map:
                self.setPanel(Panel.Extent);
                break;
              case Step.Errand:
                self.setPanel(Panel.Purpose);
                break;
              case Step.Finish:
                self.setPanel(Panel.AdditionalInformation);
                break;
              default:
                break;
            }
            break;
          case CaseType.TrafficArrangementPlan:
            if (!self.locationSpecific) {
              switch (step) {
                case 0:
                  self.setPanel(Panel.CreateTAPlan);
                  break;
                case 1:
                  self.setPanel(Panel.MarkingManager);
                  break;
                case 2:
                  self.setPanel(Panel.AdditionalInformation);
                  break;
                default:
                  break;
              }
            } else {
              switch (step) {
                case Step.Basic:
                  self.setPanel(Panel.CreateTAPlan);
                  break;
                case Step.Map:
                  self.setPanel(Panel.Extent);
                  break;
                case Step.Managers:
                  self.setPanel(Panel.MarkingManager);
                  break;
                case Step.Finish:
                  self.setPanel(Panel.AdditionalInformation);
                  break;
                default:
                  break;
              }
            }
            break;
          case CaseType.Leasehold:
            switch (step) {
              case Step.Basic:
                self.setPanel(Panel.CreateLeasehold);
                break;
              case Step.Map:
                self.setPanel(Panel.Extent);
                break;
              case Step.Finish:
                self.setPanel(Panel.AdditionalInformation);
                break;
              default:
                break;
            }
            break;
          default:
            break;
        }
      }
    },
    isStepEnabled: step => {
      return self.status >= CaseStatus.Registered || step <= self.currentStep + 1 || step <= self.touchedStep;
    },
    goToStep: (step, setFirstPanel = true) => {
      const skipValidation =
        step < self.currentStep ||
        (self.currentStep !== Step.Map &&
          (self.status > CaseStatus.Registered || window.skipValidation || self.currentStep >= step));

      if (self.form && !skipValidation) {
        self.form
          .validateFields()
          .then(() => {
            if (step > self.currentStep + 1 && step > self.touchedStep) {
              return;
            }
            if (self.currentStep === Step.Map && (self.locationSpecific || self.caseType !== 3)) {
              if (self.geometriesHasChanged || !self.geometrySnapshot) {
                if (self.points.length || self.polygons.length || self.polylines.length) {
                  self.setScreenshotEnabled(true);
                  self.setContinueToAfterScreenshot(step);
                } else {
                  self.setScreenshotEnabled(false);
                }
              } else if (!self.invalidGeometryType) {
                self.setStep(step, setFirstPanel);
              }
            } else {
              self.setStep(step, setFirstPanel);
            }
          })
          .catch(errorInfo => {
            if (errorInfo.errorFields) {
              const fieldName = errorInfo.errorFields[0].name;
              const fieldKey =
                Array.isArray(fieldName) && fieldName.length ? fieldName[fieldName.length - 1] : fieldName;
              self.setActiveKey(null); //Clear the old activeKey so the useEffect can properly open the block.
              self.setActiveKey(fieldKey);
            } else {
              console.log(errorInfo);
            }
          });
      } else {
        self.setStep(step, setFirstPanel);
      }
    },
    setForm: form => {
      self.form = form;
    },
    setMapState: currentState => {
      self.mapState = currentState;
    },
    setGeometriesHasChanged: geometriesHasChanged => {
      self.geometriesHasChanged = geometriesHasChanged;
    },
    setShowNewRegisterModal: value => {
      self.showNewRegisterModal = value;
    },
    setTouchedStep: step => {
      if (self.touchedStep < step) {
        self.touchedStep = step;
      }
    },
    setScreenshotEnabled: isEnabled => {
      self.isScreenshotEnabled = isEnabled;
    },
    setContinueToAfterScreenshot: (step = -1) => {
      self.continueToAfterScreenshot = step;
    },
    setInvalidGeometryType: value => {
      self.invalidGeometryType = value;
    },
    setChildCaseReference: caseReference => {
      self.childCases.push(caseReference);
    },
    setParentCaseReference: caseReference => {
      self.parentCases.push(caseReference);
    },
    removeParentCaseReference: () => {
      self.parentCases = [];
    },
    removeChildCaseReference: gid => {
      self.childCases = self.childCases.filter(child => child.referenceApplicationGID !== gid);
    },
    addGeometry: (geometryType, geometry) => {
      if (geometryType && geometry) {
        self.geometriesHasChanged = true;
        geometry = { ...geometry.toJSON() };
        switch (geometryType) {
          case 'point':
            self.points.push(geometry);
            break;
          case 'polyline':
            self.polylines.push(geometry);
            break;
          case 'polygon':
            self.polygons.push(geometry);
            break;
          default:
            break;
        }
      }
    },
    deleteAllGeometries: () => {
      self.points = [];
      self.polygons = [];
      self.polylines = [];
      self.geometriesHasChanged = true;
    },
    deleteGeometrySnapshot: () => {
      self.geometrySnapshot = undefined;
    },
    removeGeometry: (geometryType, gid) => {
      let removeSuccessful = false;
      switch (geometryType) {
        case 'point':
          const pointIndex = self.points.findIndex(geo => geo.gid === gid);
          if (pointIndex !== -1) {
            self.points.splice(pointIndex, 1);
            removeSuccessful = true;
          }
          break;
        case 'polyline':
          const polylineIndex = self.polylines.findIndex(geo => geo.gid === gid);
          if (polylineIndex !== -1) {
            self.polylines.splice(polylineIndex, 1);
            removeSuccessful = true;
          }
          break;
        case 'polygon':
          const polygonIndex = self.polygons.findIndex(geo => geo.gid === gid);
          if (polygonIndex !== -1) {
            self.polygons.splice(polygonIndex, 1);
            removeSuccessful = true;
          }
          break;
        default:
          break;
      }
      if (removeSuccessful) {
        self.geometriesHasChanged = true;
      }
      return removeSuccessful;
    },
    switchView: () => {
      if (self.showView === 'form') {
        self.showView = 'preview';
      } else {
        self.showView = 'form';
      }
    },
    setSketchVM: sketchVM => {
      self.sketchVM = sketchVM;
    }
  }));

export const ApplicationBaseModel = types.compose(ApplicationModel, TrackableModel).named('ApplicationBaseModel');
